/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useMemo } from 'react'

import { FiCheck, FiImage, FiX } from 'react-icons/fi'
import { RiDragMove2Line } from 'react-icons/ri'

import { type MenuAction, PageMenu } from '@/components/ui'
import Dropdown, { type DropdownOption } from '@/components/ui/dropdown/Dropdown'
import { ListControls } from '@/components/ui/list-controls/ListControls'
import { ListCounts, ListPageToolbar } from '@/components/ui/list-page/ListPageToolbar'
import { ListSearch } from '@/components/ui/list-page/ListSearch'
import { useCompany, useDashboard } from '@/hooks'
import { FilterSelect, type FilterSelectOption } from '@/pages/audit-logs/FilterSelect'
import { exportToExcel } from '@/utils'
import { companyLabelWithHierarchy } from '@/utils/companyHierarchy'

import { SNAP_GRID_PX, useColdStorage } from './ColdStorageContext'
import { ColdStorageSavedViews } from './ColdStorageSavedViews'
import { exportColumns, exportRows } from './exportRows'
import { GATEWAY_OPTIONS, STATUS_OPTIONS } from './filters'
import { OptionFilter } from './filters/OptionFilter'
import { type Units, formatTimeWithZone } from './format'

/**
 * Saved views, search, counts, then Company (the Fresh company list, as the asset map uses) and
 * Locations (a multi-select of the company's in-store assets, all picked by default) on the top
 * row; download, the Gateway and sensor Status filters and the page actions (floor plans, and the
 * edit-layout / save / cancel trio from the dashboard's layout editor) on the second.
 */
export const ColdStorageToolbar = () => {
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  const { companies, isLoading: isLoadingCompanies } = useCompany()

  const companyOptions: DropdownOption[] = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id.toString(),
        label: company.name,
        fullName: company.fullName ?? companyLabelWithHierarchy(company, companies),
      })),
    [companies]
  )
  const selectedCompanyOption = useMemo(
    () => companyOptions.find((o) => o.value === context.companyId?.toString()) ?? null,
    [companyOptions, context.companyId]
  )

  const locationOptions: FilterSelectOption[] = useMemo(
    () => context.assets.map((asset) => ({ value: asset.id.toString(), label: asset.name })),
    [context.assets]
  )

  const actions: MenuAction[] = [
    ...(!context.editMode
      ? [
          {
            id: 'floor-plans',
            label: 'Floor plans',
            icon: <FiImage />,
            disabled: !context.companyId,
            onClick: () => context.setShowFloorPlans(true),
          },
          {
            id: 'edit-layout',
            label: 'Edit layout',
            icon: <RiDragMove2Line />,
            disabled: !context.surfaceLocationId,
            tooltip: context.surfaceLocationId ? 'Drag the markers into place' : 'Pick a location first',
            onClick: () => context.setEditMode(true),
          },
        ]
      : [
          {
            id: 'save-layout',
            label: context.savingLayout ? 'Saving…' : 'Save',
            icon: <FiCheck />,
            variant: 'primary' as const,
            disabled: !context.dirty || context.savingLayout,
            onClick: () => void context.saveLayout(),
          },
          {
            id: 'cancel-edit',
            label: 'Cancel',
            icon: <FiX />,
            disabled: context.savingLayout,
            onClick: context.discardLayout,
          },
        ]),
  ]

  const handleDownload = () =>
    exportToExcel(
      exportColumns(units, context.mode.showTiming),
      exportRows(context.locations, units, context.mode.showTiming, Date.now()),
      context.mode.mode === 'blastFreezer' ? 'Cooltrax_Blast_Freezer' : 'Cooltrax_Cold_Storage'
    )

  return (
    <ListPageToolbar
      title={context.mode.title}
      sheetIsOpen={context.detailIsOpen}
      savedViews={<ColdStorageSavedViews />}
      search={
        <ListSearch
          id="search-cold-storage"
          placeholder="Search locations..."
          value={context.searchTerm}
          onChange={context.setSearchTerm}
        />
      }
      counts={
        <ListCounts
          count={`${context.locations.length.toLocaleString()} of ${context.assets.length.toLocaleString()} locations`}
          updatedAt={context.updatedAt}
          busy={context.isRefreshing}
          formatTime={formatTimeWithZone}
        />
      }
      scope={
        <div className="hidden md:flex items-center gap-4">
          <div className="w-[23rem]">
            <Dropdown
              options={companyOptions}
              selectedItem={selectedCompanyOption}
              handleSingleSelectItem={(o) => context.setCompanyId(o ? parseInt(o.value) : undefined)}
              placeholder={isLoadingCompanies ? 'Loading...' : 'Choose a company...'}
            />
          </div>
          <FilterSelect
            label="Locations"
            options={locationOptions}
            selected={context.locationIds.map(String)}
            onChange={(next) => context.setLocationIds(next.map(Number))}
            searchable
            emptyText={context.companyId ? context.mode.emptyText : 'Pick a company.'}
          />
        </div>
      }
      listControls={
        // No map here to limit to, and the list's few columns are fixed.
        <ListControls onDownload={handleDownload} disabled={context.locations.length === 0} />
      }
      filters={
        <>
          <OptionFilter
            label="Gateway"
            options={GATEWAY_OPTIONS}
            value={context.filters.gateway}
            onChange={(gateway) => context.setFilters({ ...context.filters, gateway })}
          />
          <OptionFilter
            label="Status"
            options={STATUS_OPTIONS}
            value={context.filters.status}
            onChange={(status) => context.setFilters({ ...context.filters, status })}
          />
        </>
      }
      actions={
        <>
          {context.editMode && (
            <span className="text-xs text-base-content/70">
              Drag the sensor and gateway icons into place, then Save.
            </span>
          )}
          {context.editMode && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-xs checkbox-primary"
                checked={context.snapGrid > 0}
                onChange={(e) => context.setSnapGrid(e.target.checked ? SNAP_GRID_PX : 0)}
              />
              Snap to grid
            </label>
          )}
          <PageMenu actions={actions} />
        </>
      }
    />
  )
}
